import iConv from 'iconv-lite'

/**
 * 全角から半角への変革関数
 * 入力値の英数記号を半角変換して返却
 * @param {String} strVal - 全角文字を含む文字列
 * @return {String} - 半角変換された文字列
 * https://jquery.nj-clucker.com/change-double-byte-to-half-width/
 */
export function toHalfWidth (strVal) {
  // 半角変換
  let halfVal = strVal.replace(/[！-～]/g,
    function (tmpStr) {
      // 文字コードをシフト
      return String.fromCharCode(tmpStr.charCodeAt(0) - 0xFEE0)
    }
  )

  // 文字コードシフトで対応できない文字の変換
  return halfVal.replace(/”/g, '"')
    .replace(/’/g, '\'')
    .replace(/‘/g, '`')
    .replace(/￥/g, '\\')
    .replace(/　/g, ' ') // 全角スペース
    .replace(/〜/g, '~')
}

/**
 * [関数名] containsZenkaku
 * [機　能] 全角チェック
 * [説　明] 文字列の中に全角以外の文字が含まれているか調べる
 * 　　　　 全角スペースも全角とみなす
 * [引　数] 
 * @param {String} str - 文字列
 * [返り値]
 * @return {boolean} true(含まれている) | false(含まれていない)
*/
export function containsZenkaku (str) {
  for (let i = 0; i < str.length; i++) {
      /* 1文字ずつ文字コードをエスケープし、その長さが4文字以上なら全角 */
      let len = escape(str.charAt(i)).length
      if (len >= 4) {
          // console.log('全角文字が含まれています')
          return true
      }
  }
  // console.log('全角文字は含まれていません')
  return false
}

/**
* JSONオブジェクトをCSVテキストに変換
* @param {Object} objArray - 変換するJSONオブジェクト
* @param {boolean} [option] - ヘッダ行を付加するかどうかのフラグ
* @return {String} CSV形式の文字列
*/
export function convertToCSV (objArray, option) {
  let header = (option.addHeader) ? Object.keys(objArray[0]).map(function (x) { return '"' + x + '"' }).join(',') + '\n' : ''

  let body = objArray.map(function (d) {
    return Object.keys(d).map(function (key) {
      // 数字のみなら引用符なし、それ以外は引用符付きで出力
      if (d[key] == null) { // eslint-disable-line
        return null
      } else if (/^-?[0-9]+\.?[0-9]*$/.test(d[key]) && (!Array.isArray(option.forceQuote) || option.forceQuote.indexOf(key) < 0)) {  // マイナス記号、数字(整数部)、小数点、数字(小数部)
        return d[key]
      } else {
        return '"' + d[key] + '"'
      }
    }).join(',')
  }).join('\n')

  return header + body
}

/**
* 日付の文字列変換
* @param {Date} date - 変換する日付
* @param {String} [format=null] - 書式指定
* @return {String} - 日付文字列
*/
export function formatDate (date, format) {
  if (format == null) format = 'YYYY-MM-DD hh:mm:ss.SSS'
  format = format.replace(/YYYY/g, date.getFullYear())
  format = format.replace(/MM/g, ('0' + (date.getMonth() + 1)).slice(-2))
  format = format.replace(/DD/g, ('0' + date.getDate()).slice(-2))
  format = format.replace(/hh/g, ('0' + date.getHours()).slice(-2))
  format = format.replace(/mm/g, ('0' + date.getMinutes()).slice(-2))
  format = format.replace(/ss/g, ('0' + date.getSeconds()).slice(-2))
  if (format.match(/S/g)) {
    let milliSeconds = ('00' + date.getMilliseconds()).slice(-3)
    let length = format.match(/S/g).length
    for (let i = 0; i < length; i++) format = format.replace(/S/, milliSeconds.substring(i, i + 1))
  }
  return format
}

/**
 * 全角文字を2バイト、半角文字を1バイトとしてバイト数を得る
 * @param {String} str - 文字列
 * @return {Number} - バイト数
 */
export function shiftJISLength(str) {
  let ss = iConv.encode(str, 'Shift_JIS')
  return ss.length
}

/**
 * シフトJIS文字列を指定の長さ(バイト数)以下に切り詰める
 * @param {NodeBuffer} sjisStr - シフトJIS文字列
 * @param {Number} byteLength - 切り詰める長さ(バイト数)
 * @return {NodeBuffer} - 切り詰めたシフトJIS文字列
 */
export function truncateShiftJIS(sjisStr, byteLength) {
  if (byteLength > sjisStr.length) {
    // 全部入り切っているのでそのまま返す
    return sjisStr
  }

  let i = 0
  while (i < byteLength) {
    let b = sjisStr[i]
    if ((b >= 0x81 && b <= 0x9f) || (b >= 0xe0 && b <= 0xfc)) {
      // シフトJISの第1バイト。続く1バイトは第2バイトなので読み飛ばす
      i += 2
    } else {
      i++
    }
  }
  // 切り詰め位置が2バイト文字をまたがない場合は、i === byteLength となる
  if (i > byteLength) {
    // 2バイト文字をまたいでいるので、その2バイト文字も除外する
    i = byteLength - 1
  }
  return sjisStr.slice(0, i)
}

/**
 * メンテナンス中Dlgを表示します
 * @param {Function} callback - redaxと接続しているprops
 * @param {Number} endTime - 分換算のメンテナンス終了予定時刻
 * @param {Bool} forceLogout - ログアウト処理を行うか
 */
export function showMaintenanceDlg(callback, endTime, forceLogout) {
  const message = 'メンテナンス完了予定時刻：'
    + formatDate(new Date(2000, 1, 1, endTime / 60, endTime % 60), 'hh:mm')
    + '\n（実際の完了時刻は前後する場合があります。）'
  callback('MSGBOX', { msgTitle: '現在メンテナンス中です', msgBody: message, forceLogout: forceLogout })
}



// WEBPACK FOOTER //
// ./src/utils.js